import { queryOptions } from "@tanstack/react-query";
import { createRootRouteWithContext, HeadContent, Scripts } from "@tanstack/react-router";

import { ErrorPage } from "@/components/error-page";
import { NotFound } from "@/components/not-found";
import { PostHogProvider } from "@/components/posthog-provider";
import { ThemeProvider } from "@/components/theme/theme-provider";
import { Toaster } from "@/components/ui/sonner";
import {
	createOrganizationJsonLd,
	createWebSiteJsonLd,
	DEFAULT_DESCRIPTION,
	DEFAULT_OG_IMAGE_PATH,
	DEFAULT_TITLE,
	SITE_NAME,
	toAbsoluteUrl,
} from "@/lib/seo";
import { getLocale } from "@/paraglide/runtime";
import type { RouterContext } from "@/router";
import { $getUserSession } from "@/server/actions/session";

import appCss from "../styles.css?url";
import "katex/dist/katex.min.css";
import "streamdown/styles.css";

export const Route = createRootRouteWithContext<RouterContext>()({
	head: () => ({
		meta: [
			{
				charSet: "utf-8",
			},
			{
				name: "viewport",
				content: "width=device-width, initial-scale=1",
			},
			{
				title: DEFAULT_TITLE,
			},
			{
				name: "description",
				content: DEFAULT_DESCRIPTION,
			},
			{
				name: "application-name",
				content: SITE_NAME,
			},
			{
				name: "apple-mobile-web-app-title",
				content: SITE_NAME,
			},
			{
				name: "theme-color",
				content: "#ffffff",
			},
			{
				name: "twitter:card",
				content: "summary_large_image",
			},
			{
				name: "twitter:title",
				content: DEFAULT_TITLE,
			},
			{
				name: "twitter:description",
				content: DEFAULT_DESCRIPTION,
			},
			{
				name: "twitter:image",
				content: toAbsoluteUrl(DEFAULT_OG_IMAGE_PATH),
			},
			{
				property: "og:type",
				content: "website",
			},
			{
				property: "og:site_name",
				content: SITE_NAME,
			},
			{
				property: "og:title",
				content: DEFAULT_TITLE,
			},
			{
				property: "og:description",
				content: DEFAULT_DESCRIPTION,
			},
			{
				property: "og:url",
				content: toAbsoluteUrl("/"),
			},
			{
				property: "og:image",
				content: toAbsoluteUrl(DEFAULT_OG_IMAGE_PATH),
			},
			{
				"script:ld+json": createOrganizationJsonLd(),
			},
			{
				"script:ld+json": createWebSiteJsonLd(),
			},
		],
		links: [
			{
				rel: "stylesheet",
				href: appCss,
			},
			{
				rel: "icon",
				type: "image/svg+xml",
				href: "/logo.svg",
			},
			{
				rel: "icon",
				type: "image/x-icon",
				href: "/favicon.ico",
			},
			{
				rel: "apple-touch-icon",
				href: "/logo.svg",
			},
			{
				rel: "manifest",
				href: "/manifest.json",
			},
		],
	}),
	shellComponent: RootDocument,
	errorComponent: ErrorPage,
	notFoundComponent: NotFound,
	beforeLoad: async ({ context }) => {
		const sessionResponse = await context.queryClient.ensureQueryData(
			queryOptions({
				queryKey: ["user"],
				queryFn: () => $getUserSession(),
				staleTime: 15 * 60 * 1000,
			}),
		);

		return {
			session: sessionResponse?.session,
			user: sessionResponse?.user,
		};
	},
});

function RootDocument({ children }: { children: React.ReactNode }) {
	return (
		<html lang={getLocale()} suppressHydrationWarning>
			<head>
				<HeadContent />
			</head>
			<ThemeProvider>
				<body>
					<PostHogProvider>
						<Toaster richColors position="top-right" />
						{children}
					</PostHogProvider>
					<Scripts />
				</body>
			</ThemeProvider>
		</html>
	);
}
